import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { getTripMapCameraIntent, getTripRouteFallback, getTripMapTargetPitch } from "./tripMapCamera.js";
import useTripGeolocation from "./useTripGeolocation.js";
export function useTripMapLocationState({
  useLocalStorage,
  preferSatellite,
  shape,
  stops,
  regionalDefault
}) {
  const [preferUserLocation, setPreferUserLocation,, preferenceHydrated] = useLocalStorage("prefer_user_location", false);
  const trackingEnabled = preferenceHydrated && Boolean(preferUserLocation);
  const {
    permission: geolocationPermission,
    status: geolocationStatus,
    location: userLocation
  } = useTripGeolocation({
    enabled: trackingEnabled
  });
  useEffect(() => {
    if (geolocationPermission === "denied" && preferUserLocation) {
      void setPreferUserLocation(false);
    }
  }, [geolocationPermission, preferUserLocation, setPreferUserLocation]);
  const toggleUserLocation = useCallback(() => {
    void setPreferUserLocation(!preferUserLocation);
  }, [preferUserLocation, setPreferUserLocation]);
  const routeFallback = useMemo(() => getTripRouteFallback({
    shape,
    stops,
    regionalDefault
  }), [regionalDefault, shape, stops]);
  return {
    preferenceHydrated,
    trackingEnabled,
    geolocationPermission,
    geolocating: geolocationStatus === "requesting",
    userLocation,
    isUserTrackingActive: trackingEnabled && Array.isArray(userLocation) && userLocation.length === 2,
    mapPitch: getTripMapTargetPitch({
      preferSatellite,
      trackingEnabled
    }),
    routeFallback,
    toggleUserLocation
  };
}
function normalizeBounds(coordinates) {
  const longitudes = coordinates.map(([longitude]) => longitude);
  const latitudes = coordinates.map(([, latitude]) => latitude);
  return [[Math.min(...longitudes), Math.min(...latitudes)], [Math.max(...longitudes), Math.max(...latitudes)]];
}
export function applyTripMapCameraIntent(map, intent, {
  padding,
  boundsDuration = 300,
  boundsPadding = 32
} = {}) {
  if (!map || !intent) return;
  if (intent.type === "bounds") {
    map.setPadding(padding);
    map.fitBounds(normalizeBounds(intent.bounds), {
      padding: boundsPadding,
      pitch: intent.targetPitch,
      duration: boundsDuration,
      essential: true
    });
    return;
  }
  if (intent.type === "preserve-center") {
    map.easeTo({
      pitch: intent.targetPitch,
      padding,
      duration: 250,
      essential: true
    });
    return;
  }
  const currentZoom = map.getZoom();
  const zoom = intent.zoom === 16 && currentZoom > 14 && currentZoom < 17.3 ? currentZoom : intent.zoom;
  map.easeTo({
    center: intent.center,
    pitch: intent.targetPitch,
    zoom,
    padding,
    duration: 300,
    essential: true
  });
}
export function useTripMapCamera(t0) {
  const $ = _c(28);
  const {
    mapRef,
    readyRevision,
    enabled,
    trackingEnabled,
    preferSatellite,
    vehiclePosition,
    userLocation,
    routeFallback,
    padding,
    combinedLocationDuration: t1
  } = t0;
  const combinedLocationDuration = t1 === undefined ? 700 : t1;
  const lastCameraStateRef = useRef(null);
  const [interactionMode, setInteractionMode] = useState("following");
  const [requestRevision, requestCameraSync] = useReducer(_temp, 0);
  let t2;
  if ($[0] !== padding) {
    t2 = padding ?? {};
    $[0] = padding;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    top: t3,
    bottom: t4,
    left: t5,
    right: t6
  } = t2;
  const top = t3 === undefined ? 0 : t3;
  const bottom = t4 === undefined ? 0 : t4;
  const left = t5 === undefined ? 0 : t5;
  const right = t6 === undefined ? 0 : t6;
  let t7;
  if ($[2] !== bottom || $[3] !== left || $[4] !== right || $[5] !== top) {
    t7 = {
      top,
      bottom,
      left,
      right
    };
    $[2] = bottom;
    $[3] = left;
    $[4] = right;
    $[5] = top;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const stablePadding = t7;
  let t8;
  let t9;
  if ($[7] !== mapRef || $[8] !== readyRevision) {
    t8 = () => {
      const map = mapRef.current;
      if (!map || readyRevision === 0) {
        return;
      }
      const handleInteractionStart = event => {
        if (event.originalEvent) {
          setInteractionMode("interacting");
        }
      };
      const handleInteractionEnd = event_0 => {
        if (event_0.originalEvent) {
          setInteractionMode("manual");
        }
      };
      map.on("movestart", handleInteractionStart);
      map.on("moveend", handleInteractionEnd);
      return () => {
        map.off("movestart", handleInteractionStart);
        map.off("moveend", handleInteractionEnd);
      };
    };
    t9 = [mapRef, readyRevision];
    $[7] = mapRef;
    $[8] = readyRevision;
    $[9] = t8;
    $[10] = t9;
  } else {
    t8 = $[9];
    t9 = $[10];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[11] !== combinedLocationDuration || $[12] !== enabled || $[13] !== interactionMode || $[14] !== mapRef || $[15] !== preferSatellite || $[16] !== readyRevision || $[17] !== requestRevision || $[18] !== routeFallback || $[19] !== stablePadding || $[20] !== trackingEnabled || $[21] !== userLocation || $[22] !== vehiclePosition) {
    t10 = () => {
      const map_0 = mapRef.current;
      if (!map_0 || readyRevision === 0 || !enabled) {
        return;
      }
      if (interactionMode === "interacting") {
        return;
      }
      const intent = getTripMapCameraIntent({
        mode: interactionMode === "following" ? "follow" : "manual",
        trackingEnabled,
        preferSatellite,
        vehiclePosition,
        userLocation,
        routeFallback
      });
      const intentKey = JSON.stringify({
        intent,
        readyRevision,
        requestRevision
      });
      const paddingKey = JSON.stringify(stablePadding);
      const previousCameraState = lastCameraStateRef.current;
      if (previousCameraState?.intentKey === intentKey && previousCameraState.paddingKey === paddingKey) {
        return;
      }
      lastCameraStateRef.current = {
        intentKey,
        paddingKey
      };
      const cameraIntent = previousCameraState?.intentKey === intentKey ? {
        type: "preserve-center",
        targetPitch: intent.targetPitch
      } : intent;
      applyTripMapCameraIntent(map_0, cameraIntent, {
        padding: stablePadding,
        boundsDuration: trackingEnabled && userLocation && vehiclePosition ? combinedLocationDuration : 300
      });
    };
    t11 = [combinedLocationDuration, enabled, interactionMode, mapRef, preferSatellite, readyRevision, requestRevision, routeFallback, stablePadding, trackingEnabled, userLocation, vehiclePosition];
    $[11] = combinedLocationDuration;
    $[12] = enabled;
    $[13] = interactionMode;
    $[14] = mapRef;
    $[15] = preferSatellite;
    $[16] = readyRevision;
    $[17] = requestRevision;
    $[18] = routeFallback;
    $[19] = stablePadding;
    $[20] = trackingEnabled;
    $[21] = userLocation;
    $[22] = vehiclePosition;
    $[23] = t10;
    $[24] = t11;
  } else {
    t10 = $[23];
    t11 = $[24];
  }
  useEffect(t10, t11);
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      setInteractionMode("following");
      requestCameraSync();
    };
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const recentre = t12;
  const t13 = interactionMode !== "following";
  let t14;
  if ($[26] !== t13) {
    t14 = {
      recentre,
      showRecentre: t13
    };
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
}
function _temp(revision) {
  return revision + 1;
}